import {
  ArbitrumMarketAddress,
  LrtbaseMarketAddress,
  MainnetGrandMarketAddress,
  MainnetLegacyMarketAddress,
  OptimismMarketAddress,
} from "@silo/addresses";
import {
  ChecksumAddress,
  getChecksumAddressOrThrow,
  getValueFromRecord,
  ProtocolKey,
  RecordWithDefaultValue,
} from "@silo/types";
import { createRecord } from "@silo/types";
import { SiloConfig, siloConfig } from "../utilities/silo-config-option";
import { whitelistedForProtocolKey } from "./silo-whitelist";
import { Option, pipe } from "effect";
import { PROTOCOL_CONFIG } from "@silo/config";

const SILO_CONFIGURATION: StaticSiloConfiguration = createRecord(
  {
    mainnetLegacy: createRecord<ChecksumAddress, SiloConfig>(
      {
        [MainnetLegacyMarketAddress.LQTY]: siloConfig.create({
          name_MUST_SYNCHRONIZE: "Liquity",
        }),
        [MainnetLegacyMarketAddress.FARM]: siloConfig.create({
          suppressTVL_MUST_SYNCHRONIZE: true,
        }),
        [MainnetLegacyMarketAddress.ETH_cbETH]: siloConfig.create({
          symbol_MUST_SYNCHRONIZE: "ETH/cbETH",
          name_MUST_SYNCHRONIZE: "Curve LP",
          curveLpTokenAddress_MUST_SYNCHRONIZE: getChecksumAddressOrThrow(
            "0x5b6c539b224014a09b3388e51caaa8e354c959c8"
          ),
        }),
        [MainnetLegacyMarketAddress.ETH_stETH]: siloConfig.create({
          symbol_MUST_SYNCHRONIZE: "ETH/stETH",
          name_MUST_SYNCHRONIZE: "Curve LP",
          curveLpTokenAddress_MUST_SYNCHRONIZE: getChecksumAddressOrThrow(
            "0x06325440d014e39736583c165c2963ba99faf14e"
          ),
        }),
        [MainnetLegacyMarketAddress.FRAX_USDC]: siloConfig.create({
          symbol_MUST_SYNCHRONIZE: "FRAX/USDC",
          name_MUST_SYNCHRONIZE: "Curve LP",
          curveLpTokenAddress_MUST_SYNCHRONIZE: getChecksumAddressOrThrow(
            "0x3175df0976dfa876431c2e9ee6bc45b65d3473cc"
          ),
        }),
        [MainnetLegacyMarketAddress.LUSD_3CRV]: siloConfig.create({
          symbol_MUST_SYNCHRONIZE: "LUSD/3CRV",
          name_MUST_SYNCHRONIZE: "Curve LP",
          curveLpTokenAddress_MUST_SYNCHRONIZE: getChecksumAddressOrThrow(
            "0xed279fdd11ca84beef15af5d39bb4d4bee23f0ca"
          ),
        }),
        [MainnetLegacyMarketAddress.stETH_NG]: siloConfig.create({
          symbol_MUST_SYNCHRONIZE: "ETH/stETH-ng",
          name_MUST_SYNCHRONIZE: "Curve LP",
          curveLpTokenAddress_MUST_SYNCHRONIZE: getChecksumAddressOrThrow(
            "0x21E27a5E5513D6e65C4f830167390997aA84843a"
          ),
        }),
        [MainnetLegacyMarketAddress.PTezETH]: siloConfig.create({
          symbol_MUST_SYNCHRONIZE: "PT-ezETH (25 Apr)",
          name_MUST_SYNCHRONIZE: "Renzo • Pendle",
        }),
        [MainnetLegacyMarketAddress.ezETH]: siloConfig.create({
          name_MUST_SYNCHRONIZE: "Renzo",
        }),
        [MainnetLegacyMarketAddress.PTrswETH]: siloConfig.create({
          name_MUST_SYNCHRONIZE: "Swell • Pendle",
          symbol_MUST_SYNCHRONIZE: "PT-rswETH (27 Jun)",
        }),
        [MainnetLegacyMarketAddress.rsETH]: siloConfig.create({
          name_MUST_SYNCHRONIZE: "Kelp",
        }),
        [MainnetLegacyMarketAddress.PTrsETH]: siloConfig.create({
          name_MUST_SYNCHRONIZE: "Kelp • Pendle",
          symbol_MUST_SYNCHRONIZE: "PT-rsETH (27 Jun)",
        }),
        [MainnetLegacyMarketAddress.PTweETH]: siloConfig.create({
          name_MUST_SYNCHRONIZE: "Etherfi • Pendle",
          symbol_MUST_SYNCHRONIZE: "PT-eETH (27 Jun)",
        }),
        [MainnetLegacyMarketAddress.PTezETH_DEC2024]: siloConfig.create({
          symbol_MUST_SYNCHRONIZE: "PT-ezETH (26 Dec)",
          name_MUST_SYNCHRONIZE: "Renzo • Pendle",
        }),
      },
      siloConfig.create({})
    ),
    mainnetLlama: createRecord<ChecksumAddress, SiloConfig>(
      {},
      siloConfig.create({})
    ),
    mainnetGrand: createRecord<ChecksumAddress, SiloConfig>(
      {
        [MainnetGrandMarketAddress.PT_ezETH_26SEP2024]: siloConfig.create({
          symbol_MUST_SYNCHRONIZE: "PT-ezETH (26 Sep)",
          name_MUST_SYNCHRONIZE: "Renzo • Pendle",
        }),
        [MainnetGrandMarketAddress.PT_weETH_26SEP2024]: siloConfig.create({
          name_MUST_SYNCHRONIZE: "Etherfi • Pendle",
          symbol_MUST_SYNCHRONIZE: "PT-eETH (26 Sep)",
        }),
        [MainnetGrandMarketAddress.PT_weETH_DEC2024]: siloConfig.create({
          name_MUST_SYNCHRONIZE: "Etherfi • Pendle",
          symbol_MUST_SYNCHRONIZE: "PT-eETH (26 Dec)",
        }),
        [MainnetGrandMarketAddress.PT_sUSDE_26SEP2024]: siloConfig.create({
          name_MUST_SYNCHRONIZE: "Ethena • Pendle",
          symbol_MUST_SYNCHRONIZE: "PT-sUSDE (26 Sep)",
        }),
        [MainnetGrandMarketAddress.PT_pufETH_26SEP2024]: siloConfig.create({
          name_MUST_SYNCHRONIZE: "Puffer • Pendle",
          symbol_MUST_SYNCHRONIZE: "PT-pufETH (26 Sep)",
        }),
        [MainnetGrandMarketAddress.PT_pufETH_DEC20204]: siloConfig.create({
          name_MUST_SYNCHRONIZE: "Puffer • Pendle",
          symbol_MUST_SYNCHRONIZE: "PT-pufETH (26 Dec)",
        }),
        [MainnetGrandMarketAddress.ezETH]: siloConfig.create({
          name_MUST_SYNCHRONIZE: "Renzo",
        }),
        [MainnetGrandMarketAddress.PT_rsETH_DEC2024]: siloConfig.create({
          name_MUST_SYNCHRONIZE: "Kelp • Pendle",
          symbol_MUST_SYNCHRONIZE: "PT-rsETH (26 Dec)",
        }),
      },
      siloConfig.create({})
    ),
    arbitrum: createRecord<ChecksumAddress, SiloConfig>(
      {
        [ArbitrumMarketAddress.DPX]: siloConfig.create({
          name_MUST_SYNCHRONIZE: "Dopex",
        }),
        [ArbitrumMarketAddress.SILO]: siloConfig.create({
          name_MUST_SYNCHRONIZE: "SILO",
          symbol_MUST_SYNCHRONIZE: "SILO",
        }),
        [ArbitrumMarketAddress.WOO]: siloConfig.create({
          name_MUST_SYNCHRONIZE: "WOO",
        }),
        [ArbitrumMarketAddress.PTeETH]: siloConfig.create({
          name_MUST_SYNCHRONIZE: "Etherfi • Pendle",
          symbol_MUST_SYNCHRONIZE: "PT-eETH (25 Apr)",
        }),
        [ArbitrumMarketAddress.weETH]: siloConfig.create({}),
        [ArbitrumMarketAddress.PTezETH]: siloConfig.create({
          symbol_MUST_SYNCHRONIZE: "PT-ezETH (27 Jun)",
          name_MUST_SYNCHRONIZE: "Renzo • Pendle",
        }),
        [ArbitrumMarketAddress.ezETH]: siloConfig.create({
          name_MUST_SYNCHRONIZE: "Renzo",
        }),
        [ArbitrumMarketAddress.PTweETH]: siloConfig.create({
          name_MUST_SYNCHRONIZE: "Etherfi • Pendle",
          symbol_MUST_SYNCHRONIZE: "PT-eETH (27 Jun)",
        }),
        [ArbitrumMarketAddress.PTrsETH]: siloConfig.create({
          name_MUST_SYNCHRONIZE: "Kelp • Pendle",
          symbol_MUST_SYNCHRONIZE: "PT-rsETH (27 Jun)",
        }),
        [ArbitrumMarketAddress.PTUSDe]: siloConfig.create({
          name_MUST_SYNCHRONIZE: "Ethena • Pendle",
          symbol_MUST_SYNCHRONIZE: "PT-USDe (29 Aug)",
        }),
        [ArbitrumMarketAddress.PTezETH_SEPT2024]: siloConfig.create({
          name_MUST_SYNCHRONIZE: "Renzo • Pendle",
          symbol_MUST_SYNCHRONIZE: "PT-ezETH (26 Sept)",
        }),
        [ArbitrumMarketAddress.PTweETH_SEPT2024]: siloConfig.create({
          name_MUST_SYNCHRONIZE: "Etherfi • Pendle",
          symbol_MUST_SYNCHRONIZE: "PT-eETH (26 Sept)",
        }),
        [ArbitrumMarketAddress.PTweETH_DEC2024]: siloConfig.create({
          name_MUST_SYNCHRONIZE: "Etherfi • Pendle",
          symbol_MUST_SYNCHRONIZE: "PT-eETH (26 Dec)",
        }),
        [ArbitrumMarketAddress.PTrsETH_SEPT2024]: siloConfig.create({
          name_MUST_SYNCHRONIZE: "Kelp • Pendle",
          symbol_MUST_SYNCHRONIZE: "PT-rsETH (26 Sept)",
        }),
        [ArbitrumMarketAddress.PTrsETH_DEC2024]: siloConfig.create({
          name_MUST_SYNCHRONIZE: "Kelp • Pendle",
          symbol_MUST_SYNCHRONIZE: "PT-rsETH (26 Dec)",
        }),
        [ArbitrumMarketAddress.ptUSDE_NOV2024]: siloConfig.create({
          name_MUST_SYNCHRONIZE: "Ethena • Pendle",
          symbol_MUST_SYNCHRONIZE: "PT-USDe (28 Nov)",
        }),
        [ArbitrumMarketAddress.PTweETH_JUN2025]: siloConfig.create({
          name_MUST_SYNCHRONIZE: "Etherfi • Pendle",
          symbol_MUST_SYNCHRONIZE: "PT-eETH (26 Jun 2025)",
        }),
        [ArbitrumMarketAddress.PTrsETH_JUN2025]: siloConfig.create({
          name_MUST_SYNCHRONIZE: "Kelp • Pendle",
          symbol_MUST_SYNCHRONIZE: "PT-rsETH (26 Jun 2025)",
        }),
        [ArbitrumMarketAddress.gmETH]: siloConfig.create({
          symbol_MUST_SYNCHRONIZE: "gmETH",
        }),
      },
      siloConfig.create({})
    ),
    optimism: createRecord<ChecksumAddress, SiloConfig>(
      {
        [OptimismMarketAddress.ezETH]: siloConfig.create({
          name_MUST_SYNCHRONIZE: "Renzo",
        }),
      },
      siloConfig.create({})
    ),
    base: createRecord<ChecksumAddress, SiloConfig>({}, siloConfig.create({})),
    lrtbase: createRecord<ChecksumAddress, SiloConfig>(
      {
        [LrtbaseMarketAddress.PT_LBTC]: siloConfig.create({
          name_MUST_SYNCHRONIZE: "Lombardo • Pendle",
          symbol_MUST_SYNCHRONIZE: "PT-LBTC (29 May 2025)",
        }),
      },
      siloConfig.create({})
    ),
    anvil: createRecord<ChecksumAddress, SiloConfig>({}, siloConfig.create({})),
    local: createRecord<ChecksumAddress, SiloConfig>({}, siloConfig.create({})),
  },
  createRecord({}, siloConfig.create({}))
);

/**
 * Displays these markets at the top. Only applies to the default sort order
 */
const PINNED_MARKETS: Record<ProtocolKey, ChecksumAddress[]> = {
  mainnetLegacy: PROTOCOL_CONFIG.mainnetLegacy.pinnedMarkets.map(
    getChecksumAddressOrThrow
  ),
  mainnetLlama: PROTOCOL_CONFIG.mainnetLlama.pinnedMarkets.map(
    getChecksumAddressOrThrow
  ),
  mainnetGrand: PROTOCOL_CONFIG.mainnetGrand.pinnedMarkets.map(
    getChecksumAddressOrThrow
  ),
  arbitrum: PROTOCOL_CONFIG.arbitrum.pinnedMarkets.map(
    getChecksumAddressOrThrow
  ),
  optimism: PROTOCOL_CONFIG.optimism.pinnedMarkets.map(
    getChecksumAddressOrThrow
  ),
  base: PROTOCOL_CONFIG.base.pinnedMarkets.map(getChecksumAddressOrThrow),
  lrtbase: PROTOCOL_CONFIG.lrtbase.pinnedMarkets.map(getChecksumAddressOrThrow),
  local: PROTOCOL_CONFIG.local.pinnedMarkets.map(getChecksumAddressOrThrow),
  anvil: PROTOCOL_CONFIG.anvil.pinnedMarkets.map(getChecksumAddressOrThrow),
};

type ContangoConfig =
  | { _tag: "off" }
  | { _tag: "all" }
  | { _tag: "some"; markets: ChecksumAddress[] };

export const CONTANGO_CONFIG: Record<ProtocolKey, ContangoConfig> = {
  mainnetLegacy: {
    _tag: "some",
    markets: [
      MainnetLegacyMarketAddress.weETH,
      MainnetLegacyMarketAddress.ezETH,
      MainnetLegacyMarketAddress.rsETH,
    ],
  },
  mainnetLlama: { _tag: "off" },
  mainnetGrand: {
    _tag: "some",
    markets: [
      MainnetGrandMarketAddress.amphrETH,
      MainnetGrandMarketAddress.re7LRT,
      MainnetGrandMarketAddress.rstETH,
      MainnetGrandMarketAddress.pzETH,
    ],
  },
  arbitrum: {
    _tag: "some",
    markets: [
      ArbitrumMarketAddress.PTezETH_SEPT2024,
      ArbitrumMarketAddress.PTweETH_SEPT2024,
      ArbitrumMarketAddress.PTrsETH_SEPT2024,
      ArbitrumMarketAddress.PTUSDe,
      ArbitrumMarketAddress.weETH,
      ArbitrumMarketAddress.ezETH,
      ArbitrumMarketAddress.ARB,
      ArbitrumMarketAddress.WBTC,
      ArbitrumMarketAddress.PENDLE,
      ArbitrumMarketAddress.WSTETH,
      ArbitrumMarketAddress.wUSDplus,
      ArbitrumMarketAddress.WOETH,
      ArbitrumMarketAddress.rETH,
      ArbitrumMarketAddress.ETHplus,
      ArbitrumMarketAddress.tBTC,
      ArbitrumMarketAddress.uniETH,
      ArbitrumMarketAddress.PREMIA,
      ArbitrumMarketAddress.GMX,
      ArbitrumMarketAddress.RDNT,
    ],
  },
  optimism: {
    _tag: "some",
    markets: [
      OptimismMarketAddress.ezETH,
      OptimismMarketAddress.wstETH,
      OptimismMarketAddress.OP,
      OptimismMarketAddress.WBTC,
      OptimismMarketAddress.rETH,
      OptimismMarketAddress.tBTC,
      OptimismMarketAddress.sUSD,
    ],
  },
  base: { _tag: "off" },
  lrtbase: { _tag: "off" },
  local: { _tag: "off" },
  anvil: { _tag: "off" },
};

export function getPinnedMarkets(protocolKey?: ProtocolKey) {
  return pipe(
    protocolKey,
    Option.fromNullable,
    Option.map((key) => PINNED_MARKETS[key]),
    Option.getOrElse(() => [])
  );
}

export type StaticSiloConfiguration = RecordWithDefaultValue<
  ProtocolKey,
  RecordWithDefaultValue<ChecksumAddress, SiloConfig>
>;

export const getConfigurationForSilo = (
  protocolKey: ProtocolKey,
  siloAddress: ChecksumAddress
) => {
  const protocolKeySiloConfig = getValueFromRecord(
    protocolKey,
    SILO_CONFIGURATION
  );
  const config = getValueFromRecord(siloAddress, protocolKeySiloConfig);
  const whitelisted = whitelistedForProtocolKey(siloAddress);

  return {
    ...config,
    whitelisted,
  };
};

export const getFullSiloConfiguration = (protocolKey: ProtocolKey) =>
  getValueFromRecord(protocolKey, SILO_CONFIGURATION).record;
